/**
 * Phase 130 — Reliable cross-device delete confirmation.
 *
 * Replaces `window.confirm()` for destructive actions in the
 * Library. `window.confirm` is unreliable on mobile WebViews,
 * embedded browsers, and PWAs where the browser-level prompt
 * either renders too small to read or gets silently blocked,
 * leaving users with a Delete button that appears to do nothing.
 *
 * This dialog uses the shadcn `AlertDialog` primitive (radix
 * under the hood), so it renders inside the app DOM and works
 * identically on mobile and PC.
 *
 * Usage:
 *   const [openDel, setOpenDel] = useState(null);
 *   ...
 *   <button onClick={() => setOpenDel(item)}>Delete</button>
 *   <ConfirmDeleteDialog
 *     open={!!openDel}
 *     onOpenChange={(v) => !v && setOpenDel(null)}
 *     title={`Delete "${openDel?.name}"?`}
 *     onConfirm={async () => { await api.delete(openDel.id); setOpenDel(null); }}
 *   />
 */
import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

export const ConfirmDeleteDialog = ({
  open,
  onOpenChange,
  title = "Delete this item?",
  description = "This cannot be undone.",
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  onConfirm,
  testidPrefix = "confirm-delete",
}) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm?.();
      onOpenChange?.(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="border-[#4a6984]/20 bg-white"
        data-testid={`${testidPrefix}-dialog`}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1c2d3d] font-extrabold inline-flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-rose-600" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-[#4a6984] leading-relaxed">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid={`${testidPrefix}-cancel`}
            disabled={busy}
            className="border-[#4a6984]/30"
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testidPrefix}-confirm`}
            onClick={(e) => {
              e.preventDefault();
              handle();
            }}
            disabled={busy}
            className="bg-rose-600 hover:bg-rose-700 text-white font-extrabold disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDeleteDialog;
